import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationTriangle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { APP_ROUTES } from '../config/api.config';
import { ghlCallback } from '../services/api';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%);
  padding: 20px;
`;

const SuccessCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 24px;
  padding: 40px;
  text-align: center;
  max-width: 600px;
  width: 100%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  animation: fadeIn 0.5s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Icon = styled.div<{ $error?: boolean }>`
  color: ${props => props.$error ? '#dc3545' : '#ff6b6b'};
  font-size: 64px;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  color: #1a1a1a;
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 16px;
`;

const Subtitle = styled.h2`
  color: #4b5563;
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 24px;
`;

const Message = styled.p`
  color: #6b7280;
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 32px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 16px;
  flex-wrap: wrap;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 32px;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%);
    color: white;
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
    }
  ` : `
    background: white;
    color: #ff6b6b;
    border: 2px solid #ff6b6b;
    &:hover {
      background: rgba(255, 107, 107, 0.1);
    }
  `}
`;

type CallbackStatus = 'loading' | 'success' | 'error' | 'missing_code';

const AuthSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<CallbackStatus>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  const queryParams = new URLSearchParams(location.search);
  const code = queryParams.get('code');

  useEffect(() => {
    if (!code) {
      setStatus('missing_code');
      setErrorMessage('No authorization code received from GoHighLevel. Please try connecting the location again.');
      return;
    }

    let cancelled = false;

    ghlCallback(code)
      .then(() => {
        if (cancelled) return;
        setStatus('success');
        toast.success('Location connected successfully');
        setTimeout(() => {
          navigate(APP_ROUTES.SUBSCRIBED_LOCATIONS, { replace: true });
        }, 1500);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        const message = err.message || 'Failed to complete GoHighLevel connection';
        setStatus('error');
        setErrorMessage(message);
        toast.error(message);
      });

    return () => {
      cancelled = true;
    };
  }, [code, navigate]);

  const handleBackToHome = () => {
    navigate(APP_ROUTES.SUBSCRIBED_LOCATIONS);
  };

  const handleGoToGHL = () => {
    window.open('https://app.gohighlevel.com', '_blank');
  };

  const isLoading = status === 'loading';
  const isError = status === 'error' || status === 'missing_code';

  return (
    <PageContainer>
      <SuccessCard>
        <Icon $error={isError}>
          <FontAwesomeIcon
            icon={isLoading ? faSpinner : isError ? faExclamationTriangle : faCheckCircle}
            spin={isLoading}
          />
        </Icon>
        <Title>
          {isLoading && 'Connecting your location...'}
          {status === 'success' && 'App added successfully'}
          {isError && 'Connection failed'}
        </Title>
        <Subtitle>
          {isLoading && 'Please wait while we finish setup'}
          {status === 'success' && 'Thank you for signing up with Whatsapp Bridge'}
          {isError && 'GoHighLevel authorization did not complete'}
        </Subtitle>
        <Message>
          {isLoading && 'Do not close this page.'}
          {status === 'success' && 'You can now access all the features of your WhatsApp integration. Redirecting...'}
          {isError && errorMessage}
        </Message>
        {!isLoading && (
          <ButtonGroup>
            <Button variant="secondary" onClick={handleBackToHome}>
              Back to Subscribed Locations
            </Button>
            {status === 'success' && (
              <Button variant="primary" onClick={handleGoToGHL}>
                Go to GHL
              </Button>
            )}
          </ButtonGroup>
        )}
      </SuccessCard>
    </PageContainer>
  );
};

export default AuthSuccess;
